import React, { useEffect, useState } from "react";
import api, { formatErr, formatINR } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { ImageUpload } from "../../components/ImageUpload";
import { Plus, Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";

const empty = { title: "", slug: "", category_slug: "", price: 0, original_price: null, description: "", keywords: [], inclusions: [], exclusions: [], addons: [], images: [], tags: [], active: true };

export default function AdminPackages() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/packages?limit=500").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/categories?active_only=false").then((r) => setCats(r.data)); }, []);

  const toArr = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);
  const start = (p) => {
    setEditing(p);
    setForm(p ? { ...p } : { ...empty, category_slug: cats[0]?.slug || "" });
    setOpen(true);
  };
  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, price: Number(form.price), original_price: form.original_price ? Number(form.original_price) : null };
    try {
      if (editing) await api.put(`/admin/packages/${editing.slug}`, payload);
      else await api.post("/admin/packages", payload);
      toast.success(editing ? "Updated" : "Created");
      setOpen(false); load();
    } catch (err) { toast.error(formatErr(err)); }
  };
  const remove = async (slug) => {
    if (!window.confirm("Delete this package?")) return;
    await api.delete(`/admin/packages/${slug}`); toast.success("Deleted"); load();
  };

  return (
    <div data-testid="admin-packages">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-serif-display text-4xl">Packages</h1>
        <Button onClick={() => start(null)} className="rounded-full bg-primary gap-2" data-testid="add-pkg-btn"><Plus className="w-4 h-4" /> Add Package</Button>
      </div>
      <div className="bg-background rounded-2xl border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted text-left text-xs uppercase tracking-widest">
            <tr><th className="p-3">Title</th><th className="p-3">Category</th><th className="p-3">Price</th><th className="p-3">Tags</th><th className="p-3"></th></tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.slug} className="border-t border-border">
                <td className="p-3 font-medium">{p.title}</td>
                <td className="p-3 text-muted-foreground">{p.category_slug}</td>
                <td className="p-3">{formatINR(p.price)}</td>
                <td className="p-3 text-xs">{p.tags?.join(", ")}</td>
                <td className="p-3 text-right">
                  <button onClick={() => start(p)} className="p-1 hover:text-primary" data-testid={`edit-pkg-${p.slug}`}><Edit className="w-4 h-4 inline" /></button>
                  <button onClick={() => remove(p.slug)} className="p-1 hover:text-destructive ml-2" data-testid={`del-pkg-${p.slug}`}><Trash2 className="w-4 h-4 inline" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif-display text-2xl">{editing ? "Edit" : "Add"} Package</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Title</Label><Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="pkg-title" /></div>
              <div><Label>Slug</Label><Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} /></div>
              <div>
                <Label>Category</Label>
                <select required className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" value={form.category_slug} onChange={(e) => setForm({ ...form, category_slug: e.target.value })} data-testid="pkg-category">
                  <option value="">Select...</option>
                  {cats.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                </select>
              </div>
              <div><Label>Price (₹)</Label><Input type="number" required value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="pkg-price" /></div>
              <div><Label>Original Price</Label><Input type="number" value={form.original_price || ""} onChange={(e) => setForm({ ...form, original_price: e.target.value })} /></div>
              <div><Label>Tags (comma)</Label><Input value={(form.tags || []).join(",")} onChange={(e) => setForm({ ...form, tags: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} placeholder="hot, budget, festival, trending" /></div>
            </div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div><Label>Keywords (comma)</Label><Input value={(form.keywords || []).join(",")} onChange={(e) => setForm({ ...form, keywords: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} /></div>
            <div><Label>Inclusions (one per line)</Label><Textarea value={(form.inclusions || []).join("\n")} onChange={(e) => setForm({ ...form, inclusions: toArr(e.target.value) })} /></div>
            <div><Label>Exclusions (one per line)</Label><Textarea value={(form.exclusions || []).join("\n")} onChange={(e) => setForm({ ...form, exclusions: toArr(e.target.value) })} /></div>
            <div><Label>Add-ons (name|price per line)</Label>
              <Textarea
                value={(form.addons || []).map((a) => `${a.name}|${a.price}`).join("\n")}
                onChange={(e) => setForm({ ...form, addons: toArr(e.target.value).map((l) => { const [name, price] = l.split("|"); return { name: name.trim(), price: Number(price) || 0 }; }) })}
              />
            </div>
            <div><Label>Image URLs (one per line) — paste external URLs (Unsplash etc) here</Label><Textarea value={(form.images || []).join("\n")} onChange={(e) => setForm({ ...form, images: toArr(e.target.value) })} /></div>
            <ImageUpload
              label="Or Upload Images"
              multiple
              value={form.images || []}
              onChange={(v) => setForm({ ...form, images: v })}
              testId="pkg-image-upload"
            />
            <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /><Label>Active</Label></div>
            <Button type="submit" className="w-full rounded-full bg-primary" data-testid="pkg-save">Save</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
